// DE subscriptions as a table (Setup › DE subscriptions and the DE profile's Subscriptions tab),
// with "Edit" per row and the create dialog. `GET /de-subscriptions` is manager-only and has no
// DE filter, so the profile tab filters the loaded pages to its DE and says when more pages exist.
import {
  Badge,
  EmptyState,
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  WorkerBadge,
  workerName,
} from "@companyos/ui";
import { useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { CalendarXIcon, CreditCardIcon, PencilIcon } from "lucide-react";
import type { Api } from "../../api/client";
import type { DeProfile, DeSubscription } from "../../api/types";
import type { CompanyAccess } from "../company";
import { ConfirmRowAction } from "../confirm-row-action";
import { formatMoney } from "../format";
import { keys, subscriptionsQuery } from "../queries";
import { LoadError, Loading, LoadMore } from "../screen-parts";
import { DENSE_TABLE, RowActions } from "../settings/row-actions";
import { SubscriptionDialog } from "./subscription-dialog";

const thisMonth = () => new Date().toISOString().slice(0, 7);

/** Running, scheduled or ended, as of this month. */
export function subscriptionState(s: DeSubscription, month = thisMonth()) {
  if (s.start_month > month) return { label: "Scheduled", tone: "info" as const };
  if (s.end_month && s.end_month < month) return { label: "Ended", tone: "neutral" as const };
  return { label: "Running", tone: "success" as const };
}

export function SubscriptionsTable({
  api,
  company,
  rows,
  deName,
  caption,
}: {
  api: Api;
  company: CompanyAccess;
  rows: DeSubscription[];
  /** The DE column; omitted on a single DE's tab. */
  deName?: (membershipId: string) => string;
  caption: string;
}) {
  const operator = company.roles.includes("agency_operator");
  const month = thisMonth();
  const qc = useQueryClient();
  // A direct "end": the same PATCH as the dialog's Until field, set to this month.
  const end = useMutation({
    mutationFn: (s: DeSubscription) =>
      api.client.deSubscriptions.update({
        params: { co: company.id, id: s.id },
        body: { end_month: month },
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.subscriptions(company.id) }),
  });
  return (
    <Table data-slot="de-subscriptions" className={DENSE_TABLE}>
      <TableCaption className="sr-only">{caption}</TableCaption>
      <TableHeader>
        <TableRow>
          {deName ? <TableHead scope="col">Digital employee</TableHead> : null}
          <TableHead scope="col" className="text-right">
            Monthly
          </TableHead>
          <TableHead scope="col">From</TableHead>
          <TableHead scope="col" className="max-md:hidden">
            Until
          </TableHead>
          <TableHead scope="col" className="max-md:hidden">
            Visible to
          </TableHead>
          <TableHead scope="col">State</TableHead>
          <TableHead scope="col">
            <span className="sr-only">Actions</span>
          </TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((s) => {
          const state = subscriptionState(s);
          const who = deName ? deName(s.membership_id) : null;
          return (
            <TableRow key={s.id} data-subscription={s.id} className="group/row">
              {who !== null ? (
                <TableHead scope="row" className="font-medium">
                  <span className="flex items-center gap-1.5">
                    <WorkerBadge kind="de" size="sm" />
                    {workerName(who)}
                  </span>
                </TableHead>
              ) : null}
              <TableCell className="text-right tabular-nums" data-field="amount">
                {formatMoney(s.amount_minor, s.currency)}
              </TableCell>
              <TableCell>{s.start_month}</TableCell>
              <TableCell data-field="end" className="max-md:hidden">
                {s.end_month ?? "—"}
              </TableCell>
              <TableCell className="max-md:hidden">
                {s.scope === "agency" ? "Agency only" : "Client and agency"}
              </TableCell>
              <TableCell>
                <Badge variant={state.tone}>{state.label}</Badge>
              </TableCell>
              <TableCell className="w-px text-right">
                <RowActions>
                  <SubscriptionDialog
                    api={api}
                    companyId={company.id}
                    operator={operator}
                    existing={s}
                    trigger={{
                      label: "Edit",
                      name: `Edit subscription${who ? ` of ${who}` : ""} from ${s.start_month}`,
                      variant: "ghost",
                      size: "icon-sm",
                      icon: PencilIcon,
                      className: "text-muted-foreground hover:text-foreground",
                    }}
                  />
                  {state.label === "Running" && (!s.end_month || s.end_month > month) ? (
                    <ConfirmRowAction
                      icon={CalendarXIcon}
                      label={`End subscription${who ? ` of ${who}` : ""} from ${s.start_month} this month`}
                      tip="End this month"
                      disabled={end.isPending}
                      title="End this subscription this month?"
                      description={`${month} is its last month. Months already posted stay as they are.`}
                      confirmLabel="End this month"
                      onConfirm={() => end.mutate(s)}
                    />
                  ) : null}
                </RowActions>
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}

export function DeSubscriptionsTab({
  api,
  company,
  de,
}: {
  api: Api;
  company: CompanyAccess;
  de: DeProfile;
}) {
  const q = useInfiniteQuery(subscriptionsQuery(api, company.id));
  if (q.isPending) return <Loading label="Loading subscriptions" rows={2} />;
  if (q.isError) return <LoadError what="Subscriptions" error={q.error} />;
  const rows = q.data.pages
    .flatMap((p) => p.data)
    .filter((s) => s.membership_id === de.membership_id);
  const create = de.seat ? (
    <SubscriptionDialog
      api={api}
      companyId={company.id}
      operator={company.roles.includes("agency_operator")}
      des={[{ id: de.membership_id, name: workerName(de.display_name), seatId: de.seat.id }]}
      trigger={{ label: "New subscription" }}
    />
  ) : null;
  return (
    <div className="flex min-w-0 flex-col gap-4" data-slot="de-subscriptions-tab">
      {rows.length ? (
        <>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-sm text-muted-foreground">
              {workerName(de.display_name)}'s monthly fee. One cost row is posted per month.
            </p>
            {create}
          </div>
          <SubscriptionsTable
            api={api}
            company={company}
            rows={rows}
            caption={`${workerName(de.display_name)}'s subscriptions.`}
          />
        </>
      ) : (
        <EmptyState
          variant="card"
          icon={CreditCardIcon}
          title={q.hasNextPage ? "None in the loaded subscriptions" : "No subscription yet"}
          description={
            de.seat
              ? `Add ${workerName(de.display_name)}'s monthly fee: it is posted to the cost ledger each month.`
              : `${workerName(de.display_name)} has no seat, so no subscription can be added.`
          }
          actions={create}
        />
      )}
      <LoadMore
        what="subscriptions"
        hasMore={q.hasNextPage}
        loading={q.isFetchingNextPage}
        onMore={() => void q.fetchNextPage()}
      />
    </div>
  );
}
